import type { Chain } from '@/types'

/**
 * Derive the destination RECIPIENT address for a bridge.
 *
 * FUND-CRITICAL: this is the mint recipient encoded into the signed burn — the single most fund-critical
 * field on the screen. The see-what-you-sign invariant requires the value the UI shows here to be exactly
 * the value that gets signed as the recipient.
 *
 * Precedence:
 * - When the user EXPLICITLY chose a recipient (RecipientModal → `destManuallyChosen`), that address WINS.
 *   The ambient connected account must never shadow it (that shadowing routed funds to the source — #107).
 * - Otherwise the recipient defaults to the connected wallet for the destination chain (the self-bridge
 *   case), with the auto-synced `destinationAddress` as a fallback for the render before the wallet-sync
 *   effect runs. Returns null when nothing is available.
 */
export function deriveDestinationRecipient(args: {
  destManuallyChosen: boolean
  destinationAddress: string
  destinationChain: Chain | null
  evmAddress: string | null
  solanaAddress: string | null
  tronAddress: string | null
}): string | null {
  const { destManuallyChosen, destinationAddress, destinationChain, evmAddress, solanaAddress, tronAddress } = args
  // Explicit choice always wins — never fall back to a connected (source) account.
  if (destManuallyChosen) return destinationAddress || null
  if (destinationChain === 'tron') return tronAddress ?? (destinationAddress || null)
  if (destinationChain === 'solana') return solanaAddress ?? (destinationAddress || null)
  return evmAddress ?? (destinationAddress || null)
}
